import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOrderStatus } from '../lib/order.js';
import { useCart } from '../lib/cart.js';

export function OrderSuccessPage(): JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const status = useOrderStatus(orderId);
  const cart = useCart();

  // Clear the cart as soon as the order is paid (any non-pending state
  // indicates Stripe took the money successfully or the order is final).
  // We do NOT wait for fulfilled because a paid-but-unfulfilled order is
  // still "paid" from the dealer's POV; an admin will retry-generate.
  useEffect(() => {
    const s = status.data?.status;
    if (s && s !== 'pending') cart.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.status]);

  if (!orderId) {
    return <p className="text-sm text-slate-500">Missing order id.</p>;
  }

  const s = status.data?.status;
  return (
    <article data-testid="order-success" className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Order placed</h1>
      <p className="text-sm text-slate-600">
        Order <code data-testid="order-id" className="rounded bg-slate-100 px-1 py-0.5 text-xs">{orderId}</code>
      </p>
      {!status.data ? (
        <p data-testid="order-loading" className="rounded bg-slate-100 p-4 text-sm text-slate-700">
          Checking order status...
        </p>
      ) : s === 'pending' ? (
        <p data-testid="order-pending" className="rounded bg-amber-50 p-4 text-sm text-amber-800">
          Stripe is confirming your payment. This page will update automatically.
        </p>
      ) : s === 'paid-but-unfulfilled' ? (
        <p data-testid="order-paid-unfulfilled" className="rounded bg-amber-50 p-4 text-sm text-amber-800">
          Your payment went through, but a generator step is still finishing. A Z9 admin will follow up; refresh in a few minutes.
        </p>
      ) : s === 'fulfilled' ? (
        <div data-testid="order-fulfilled" className="space-y-3">
          <p className="rounded bg-green-50 p-4 text-sm text-green-800">
            Your licenses are ready. Save each file alongside your install -- you can also re-download from your dashboard later.
          </p>
          <ul className="space-y-2">
            {(status.data.issuanceIds ?? []).map((id) => (
              <li key={id}>
                <a
                  href={`/api/licenses/${encodeURIComponent(id)}/download`}
                  data-testid={`order-download-${id}`}
                  className="text-sm text-slate-900 underline hover:text-slate-700"
                  download
                >
                  Download {id}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : s === 'cancelled' ? (
        <p data-testid="order-cancelled" className="rounded bg-slate-100 p-4 text-sm text-slate-700">
          This order was cancelled before payment cleared.{' '}
          <Link to="/" className="font-medium text-slate-900 underline">
            Browse the catalog
          </Link>{' '}
          to start again.
        </p>
      ) : s === 'refunded' ? (
        <p data-testid="order-refunded" className="rounded bg-slate-100 p-4 text-sm text-slate-700">
          This order has been refunded.
        </p>
      ) : null}
    </article>
  );
}
